/**
 * 카테고리 카탈로그 — CloudFront에서 직접 받는다.
 *
 * **API를 거치지 않는다.** Lambda 호출도 비용도 0이고, 엣지에서 캐시된다.
 * 서버는 같은 빌드 산출물을 번들해 검증에만 쓴다(계획 §7).
 *
 * 언어별로 완결된 파일이라 하나만 받으면 된다.
 *
 * 목록은 **평탄하다.** 그룹도 트리도 없다 — 화면이 검색 전용이라 훑어 내려갈
 * 일이 없고, 이름이 전부 유일해서 경로로 구분할 것도 없다.
 */

import {
  searchCategories,
  type CategoryCatalog,
  type CategoryNode,
  type Language,
  type SearchResult,
} from '@lifetimeline/shared';

/** 언어별로 한 번만 받아 캐시한다 */
const cache = new Map<Language, Promise<LoadedCatalog>>();

export interface LoadedCatalog {
  language: Language;
  categories: CategoryNode[];
  byId: ReadonlyMap<string, CategoryNode>;
}

async function fetchCatalog(language: Language): Promise<LoadedCatalog> {
  const response = await fetch(`/data/categories/${language}.json`);
  if (!response.ok) {
    throw new Error(`카테고리를 불러오지 못했습니다 (${response.status})`);
  }
  const catalog = (await response.json()) as CategoryCatalog;
  return {
    language,
    categories: catalog.categories,
    byId: new Map(catalog.categories.map((category) => [category.id, category])),
  };
}

export function loadCatalog(language: Language): Promise<LoadedCatalog> {
  const cached = cache.get(language);
  if (cached !== undefined) {
    return cached;
  }
  const pending = fetchCatalog(language).catch((error: unknown) => {
    // 실패를 캐시하면 새로고침 전까지 영영 못 받는다
    cache.delete(language);
    throw error;
  });
  cache.set(language, pending);
  return pending;
}

export function search(
  catalog: LoadedCatalog,
  query: string,
  limit = 12,
): SearchResult<CategoryNode>[] {
  return searchCategories(query, catalog.categories, { limit });
}

/**
 * 카테고리 이름. 카탈로그에 없으면 id를 그대로 보여준다.
 *
 * null을 돌려주지 않는 이유: 이름을 못 찾았다고 Pick을 숨기면 사용자 데이터가
 * 사라진 것처럼 보인다. 보여주되 무엇인지 알 수 있게 id라도 남긴다.
 */
export function categoryName(catalog: LoadedCatalog, categoryId: string): string {
  return catalog.byId.get(categoryId)?.name ?? categoryId;
}

/**
 * 검색창 placeholder에 돌려 쓸 예시 이름.
 *
 * 고를 수 있는 목록을 보여주지 않기로 했으므로(보이는 것 중에서 고르게 되니까),
 * 처음 온 사람이 무엇을 칠 수 있는지 알 방법이 placeholder뿐이다. 빈 입력창만
 * 있으면 거기서 멈춘다.
 *
 * 매번 같은 예시를 주지 않으려고 무작위로 뽑는다 — 목록의 앞쪽만 계속 노출되면
 * 그게 곧 축소판 목록이 된다.
 */
export function sampleNames(catalog: LoadedCatalog, count: number): string[] {
  const active = catalog.categories.filter((category) => category.status === 'active');
  const picked: string[] = [];
  const used = new Set<number>();

  while (picked.length < count && used.size < active.length) {
    const index = Math.floor(Math.random() * active.length);
    if (used.has(index)) {
      continue;
    }
    used.add(index);
    const name = active[index]?.name;
    if (name !== undefined) {
      picked.push(name);
    }
  }
  return picked;
}
